import { useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import type { InventoryItem, Product } from "@/types";
import { categoryBadge } from "@/utils/categories";
import { formatCOP, formatNumber } from "@/utils/format";

interface CatalogVisualProps {
  products: Product[];
  inventory: InventoryItem[];
  // Si se entrega el id del asesor, la vista muestra sus unidades disponibles.
  advisorId?: string;
  // Categorías configuradas (para el filtro). Si no, se toman del catálogo.
  categories?: string[];
}

// Catálogo visual de solo lectura: tarjetas con foto, categoría, SKU, nombre,
// precio público, PV y (para el asesor) sus unidades disponibles.
// La utilidad y el costo NO viven aquí: eso se consulta en Inventario y Reportes.
const CatalogVisual = ({ products, inventory, advisorId, categories }: CatalogVisualProps) => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>("Todas");
  // Ventana flotante para ver la imagen del producto en grande.
  const [preview, setPreview] = useState<Product | null>(null);

  const categoryFilters = useMemo(() => {
    const configured = Array.isArray(categories) && categories.length > 0 ? categories : null;
    const base = configured ?? Array.from(new Set(products.map((p) => p.category).filter(Boolean)));
    return ["Todas", ...base];
  }, [categories, products]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      const matchCategory = category === "Todas" || p.category === category;
      const matchQuery =
        !q || p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q);
      return matchCategory && matchQuery;
    });
  }, [products, query, category]);

  const unitsFor = (productId: string): number | null => {
    if (!advisorId) return null;
    const inv = inventory.find((i) => i.productId === productId);
    if (!inv) return 0;
    return (inv.stockByAdvisor[advisorId] ?? 0) + (inv.stockOwnedByAdvisor?.[advisorId] ?? 0);
  };

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-accent-300">
            <i className="ri-search-line" />
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre o SKU..."
            className="w-full bg-background-100/70 border border-background-300/50 rounded-md pl-9 pr-3 py-2 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/50 focus:ring-2 focus:ring-accent-500/25"
            aria-label="Buscar en el catálogo"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {categoryFilters.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border cursor-pointer whitespace-nowrap transition-colors ${
                category === c
                  ? "bg-accent-500/20 border-accent-500/40 text-accent-200"
                  : "border-background-300/50 text-foreground-600 hover:border-accent-500/30"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="glass-panel rounded-lg p-10 text-center">
          <span className="mx-auto w-14 h-14 rounded-lg bg-accent-500/15 text-accent-200 flex items-center justify-center mb-4">
            <i className="ri-search-eye-line text-2xl" />
          </span>
          <h3 className="editable-title text-base font-bold">Sin productos para mostrar</h3>
          <p className="text-xs text-foreground-600 mt-1">
            Ajusta la búsqueda o la categoría para ver el catálogo.
          </p>
        </div>
      ) : (
        <div
          data-product-shop
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 md:gap-4"
        >
          {filtered.map((p) => {
            const units = unitsFor(p.id);
            return (
              <article
                key={p.id || p.sku}
                className="glass-panel rounded-lg overflow-hidden flex flex-col animate-fade-up hover:border-accent-500/40 transition-colors"
              >
                <div
                  onClick={() => setPreview(p)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setPreview(p);
                  }}
                  className="relative w-full h-40 bg-background-200/40 flex items-center justify-center overflow-hidden cursor-pointer group"
                  title="Ver la imagen en grande"
                >
                  {p.imageUrl ? (
                    <img
                      src={p.imageUrl}
                      alt={p.name}
                      title={`Amway · ${p.name}`}
                      className="w-full h-full object-contain p-2 transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <i className="ri-image-2-line text-3xl text-foreground-500" />
                  )}
                  <span className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-background-950/55 text-foreground-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-zoom-in-line text-sm" />
                  </span>
                  <span
                    className={`absolute top-2 left-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${categoryBadge(p.category)}`}
                  >
                    {p.category}
                  </span>
                  {units !== null && (
                    <span
                      className={`absolute top-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        units > 0
                          ? "bg-accent-500/30 text-accent-100"
                          : "bg-background-300/60 text-foreground-600"
                      }`}
                    >
                      {units > 0 ? `${formatNumber(units)} und` : "Sin stock"}
                    </span>
                  )}
                </div>

                <div className="p-4 flex flex-col flex-1">
                  <p className="text-[11px] font-mono text-accent-200">{p.sku}</p>
                  <h3 className="editable-title text-sm font-bold mt-1 leading-snug">
                    {p.name}
                  </h3>

                  <div className="mt-auto pt-3">
                    <div className="flex items-end justify-between gap-2">
                      <div>
                        <p className="text-[10px] text-foreground-500">Precio público</p>
                        <p className="text-base font-heading font-extrabold text-foreground-950">
                          {formatCOP(p.pricePublic)}
                        </p>
                      </div>
                      <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-secondary-500/20 text-secondary-100">
                        {formatNumber(p.pv)} PV
                      </span>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <p className="text-[11px] text-foreground-500 mt-4 flex items-center gap-1.5">
        <i className="ri-information-line" /> {formatNumber(filtered.length)} referencias en el
        catálogo
      </p>

      <Modal
        open={preview !== null}
        onClose={() => setPreview(null)}
        title={preview?.name ?? "Producto"}
        icon="ri-image-2-line"
      >
        <div className="w-full h-[55vh] rounded-lg bg-background-200/40 border border-background-300/40 flex items-center justify-center overflow-hidden">
          {preview?.imageUrl ? (
            <img
              src={preview.imageUrl}
              alt={preview.name}
              title={`Amway · ${preview.name}`}
              className="w-full h-full object-contain p-3"
            />
          ) : (
            <span className="flex flex-col items-center gap-2 text-foreground-500">
              <i className="ri-image-2-line text-4xl" />
              <span className="text-xs">Este producto aún no tiene foto.</span>
            </span>
          )}
        </div>
        {preview && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-mono text-accent-200">{preview.sku}</p>
              <p className="text-sm font-semibold text-foreground-950">{preview.name}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-base font-heading font-extrabold text-foreground-950">
                {formatCOP(preview.pricePublic)}
              </span>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-secondary-500/20 text-secondary-100">
                {formatNumber(preview.pv)} PV
              </span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CatalogVisual;